import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  InputGroup
} from 'react-bootstrap';
import { Pencil, Trash2, Plus } from 'react-bootstrap-icons';
import { FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DataApi from '../../../api/DataApi';
import AppToast from '../../AppToast';
import AppIconDisplay from '../../layout/AppIconDisplay';
import { getEntitlementsSection } from '../../../utils/entitlements';

// Build app tabs list from config: prefer config.tabs (name or id), else config.tabIds (legacy ids).
// Use name for migration (target org resolves by name); id kept for backward compat.
const configToAppTabs = (config) => {
  if (!config) return [];
  const tabs = Array.isArray(config.tabs) ? config.tabs : [];
  if (tabs.length > 0) {
    return tabs.map((t) => ({
      name: t.name != null ? String(t.name).trim() || undefined : undefined,
      id: t.id != null ? String(t.id).trim() || undefined : undefined,
      group_key: t.group_key ?? '',
      group_label: t.group_label ?? ''
    }));
  }
  const ids = Array.isArray(config.tabIds) ? config.tabIds : [];
  return ids.filter((id) => id != null && String(id).trim() !== '').map((id) => ({ id: String(id).trim(), group_key: '', group_label: '' }));
};

// config.slotOrder: [{ type: 'tab', name?, id? } | { type: 'group', group_key }] — order of slots in menu (allows group between tabs).
const configToSlotOrder = (config, appTabs) => {
  if (!config || !Array.isArray(appTabs) || appTabs.length === 0) return [];
  const raw = config.slotOrder;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((s) => {
      if (s?.type === 'group' && s?.group_key != null) return { type: 'group', group_key: String(s.group_key).trim() };
      if (s?.type === 'tab') return { type: 'tab', name: s.name != null ? String(s.name).trim() || undefined : undefined, id: s.id != null ? String(s.id) : undefined };
      return null;
    }).filter(Boolean);
  }
  // Derive: no-group tabs first (each one slot), then one slot per group.
  const order = [];
  const groupSeen = new Set();
  appTabs.forEach((entry) => {
    const gk = (entry.group_key || '').trim();
    if (!gk) {
      order.push({ type: 'tab', name: entry.name, id: entry.id });
      return;
    }
    if (groupSeen.has(gk)) return;
    groupSeen.add(gk);
    order.push({ type: 'group', group_key: gk });
  });
  return order;
};

const AppManager = () => {
  const [apps, setApps] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddTabsModal, setShowAddTabsModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_standard: false,
    icon: '', // '' | 'preset:default' | 'preset:sales' | ... | 'custom'
    iconCustomUrl: '', // when icon === 'custom'
    appTabs: [], // [{ name, id?, group_key?, group_label? }, ...] name preferred for migration
    slotOrder: [] // [{ type: 'tab', name?, id? } | { type: 'group', group_key }] — menu bar order (group between tabs)
  });

  const appsApi = new DataApi('apps');
  const tabsApi = new DataApi('tabs');

  const fieldConfig = getEntitlementsSection('field', {});
  const allowStandardDelete = fieldConfig?.allowStandardDelete === true;
  const canDeleteApp = (app) => !app?.is_standard || allowStandardDelete;

  const appConfig = getEntitlementsSection('app', {});
  const allowCreateApp = appConfig?.allowCreateApp !== false;
  const maxApps = Number(appConfig?.maxApps);
  const appCount = apps.filter((a) => !a?.is_deleted).length;
  const isAppLimitReached = Number.isFinite(maxApps) && maxApps >= 0 && appCount >= maxApps;
  const canCreateApp = allowCreateApp && !isAppLimitReached;

  useEffect(() => {
    loadApps();
    loadTabs();
  }, []);

  const loadApps = async () => {
    try {
      setLoading(true);
      const res = await appsApi.fetchAll();
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setApps(res.data.data);
      } else {
        setApps([]);
      }
    } catch (err) {
      console.error('Error loading apps:', err);
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to load apps', type: 'danger' });
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTabs = async () => {
    try {
      const res = await tabsApi.fetchAll('is_active=true');
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setTabs(res.data.data);
      } else {
        setTabs([]);
      }
    } catch (err) {
      console.error('Error loading tabs:', err);
      setTabs([]);
    }
  };

  const openCreate = () => {
    setEditingApp(null);
    setFormData({ name: '', description: '', is_standard: false, icon: '', iconCustomUrl: '', appTabs: [], slotOrder: [] });
    setShowModal(true);
  };

  const openEdit = (app) => {
    setEditingApp(app);
    const appTabs = configToAppTabs(app?.config);
    const rawIcon = (app?.config?.icon != null ? String(app.config.icon) : '').trim() || '';
    const isPreset = rawIcon.startsWith('preset:');
    const icon = isPreset ? rawIcon : (rawIcon ? 'custom' : '');
    const iconCustomUrl = rawIcon && !isPreset ? rawIcon : '';
    const slotOrder = configToSlotOrder(app?.config, appTabs);
    setFormData({
      name: app.name || '',
      description: app.description || '',
      is_standard: !!app.is_standard,
      icon,
      iconCustomUrl,
      appTabs: appTabs.map((t) => ({
        name: t.name || (t.id ? getTabById(t.id)?.name : undefined) || t.id || '',
        id: t.id,
        group_key: t.group_key || '',
        group_label: t.group_label || ''
      })),
      slotOrder
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowAddTabsModal(false);
    setEditingApp(null);
    setFormData({ name: '', description: '', is_standard: false, icon: '', iconCustomUrl: '', appTabs: [], slotOrder: [] });
  };

  const handleSave = async () => {
    const name = (formData.name || '').trim();
    if (!name) {
      setToast({ show: true, message: 'Name is required', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const tabsPayload = formData.appTabs.map((t) => {
        const name = (t.name || (t.id ? getTabById(t.id)?.name : null) || '').trim();
        let gk = (t.group_key || '').trim();
        const gl = (t.group_label || '').trim();
        if (gk === '__new__') gk = '';
        const out = name ? { name } : { id: t.id };
        if (gk) Object.assign(out, { group_key: gk, group_label: gl || gk });
        return out;
      }).filter((t) => t.name || t.id);
      const iconVal =
        formData.icon === 'custom'
          ? (formData.iconCustomUrl || '').trim() || undefined
          : (formData.icon || '').trim() || undefined;
      const slotOrderPayload = (formData.slotOrder || []).length > 0
        ? formData.slotOrder.map((s) => (s.type === 'group' ? { type: 'group', group_key: s.group_key } : { type: 'tab', name: s.name, id: s.id }))
        : undefined;
      const baseConfig = editingApp?.config ? { ...editingApp.config } : {};
      const payload = {
        name,
        description: (formData.description || '').trim() || undefined,
        is_standard: !!formData.is_standard,
        config: { ...baseConfig, tabs: tabsPayload, slotOrder: slotOrderPayload ?? null, icon: iconVal ?? null }
      };
      if (editingApp) {
        await appsApi.update(payload, editingApp.id);
        setToast({ show: true, message: 'App updated successfully', type: 'success' });
      } else {
        await appsApi.create(payload);
        setToast({ show: true, message: 'App created successfully', type: 'success' });
      }
      handleCloseModal();
      loadApps();
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || (editingApp ? 'Failed to update app' : 'Failed to create app'),
        type: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (app) => {
    if (!canDeleteApp(app)) {
      setToast({ show: true, message: 'Standard apps cannot be deleted when Allow Standard Delete is disabled for your license.', type: 'warning' });
      return;
    }
    setAppToDelete(app);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!appToDelete) return;
    if (!canDeleteApp(appToDelete)) {
      setToast({ show: true, message: 'Standard apps cannot be deleted.', type: 'warning' });
      setShowDeleteModal(false);
      setAppToDelete(null);
      return;
    }
    setSaving(true);
    try {
      await appsApi.delete(appToDelete.id);
      setToast({ show: true, message: 'App deleted successfully', type: 'success' });
      setShowDeleteModal(false);
      setAppToDelete(null);
      loadApps();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to delete app', type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const getTabById = (tabId) => tabs.find((x) => x.id === tabId);
  const getTabByName = (tabName) => tabs.find((x) => x.name === tabName);

  const getTabByEntry = (entry) => {
    if (entry?.name) return getTabByName(entry.name) || tabs.find((x) => (x.label || '').trim() === (entry.name || '').trim());
    if (entry?.id) return getTabById(entry.id);
    return null;
  };

  const getTabLabel = (tabId) => {
    const t = getTabById(tabId);
    return t ? (t.label || t.name || tabId) : tabId;
  };

  const getTabLabelByEntry = (entry) => {
    const t = getTabByEntry(entry);
    return t ? (t.label || t.name) : (entry?.name || entry?.id || '—');
  };

  // Unique groups in current app tabs (for dropdown)
  const existingGroupsInApp = useMemo(() => {
    const seen = new Set();
    const list = [];
    formData.appTabs.forEach((entry) => {
      const gk = (entry.group_key || '').trim();
      if (!gk || gk === '__new__' || seen.has(gk.toLowerCase())) return;
      seen.add(gk.toLowerCase());
      const gl = (entry.group_label || '').trim() || gk;
      list.push({ group_key: gk, group_label: gl });
    });
    return list.sort((a, b) => (a.group_label || a.group_key).localeCompare(b.group_label || b.group_key));
  }, [formData.appTabs]);

  const setAppTabGroup = (index, group_key, group_label) => {
    setFormData((prev) => {
      const next = [...prev.appTabs];
      if (index < 0 || index >= next.length) return prev;
      const entry = next[index];
      const gk = (group_key || '').trim();
      next[index] = { ...entry, group_key: gk, group_label: (group_label || gk || '').trim() || '' };
      let newOrder = [...(prev.slotOrder || [])];
      const tabSlotMatch = (s) => s.type === 'tab' && (s.name === entry.name || String(s.id) === String(entry.id));
      if (gk && gk !== '__new__') {
        newOrder = newOrder.filter((s) => !tabSlotMatch(s));
        if (!newOrder.some((s) => s.type === 'group' && s.group_key === gk)) newOrder = [...newOrder, { type: 'group', group_key: gk }];
      } else {
        if (!newOrder.some(tabSlotMatch)) newOrder = [...newOrder, { type: 'tab', name: entry.name, id: entry.id }];
      }
      return { ...prev, appTabs: next, slotOrder: newOrder };
    });
  };

  const removeAppTab = (index) => {
    setFormData((prev) => {
      const entry = prev.appTabs[index];
      if (!entry) return prev;
      const newTabs = prev.appTabs.filter((_, i) => i !== index);
      let newOrder = prev.slotOrder || [];
      const gk = (entry.group_key || '').trim();
      if (!gk) {
        const tabSlot = (s) => s.type === 'tab' && (s.name === entry.name || String(s.id) === String(entry.id));
        const idx = newOrder.findIndex(tabSlot);
        if (idx >= 0) newOrder = newOrder.slice(0, idx).concat(newOrder.slice(idx + 1));
      } else if (!newTabs.some((e) => (e.group_key || '').trim() === gk)) {
        newOrder = newOrder.filter((s) => !(s.type === 'group' && s.group_key === gk));
      }
      return { ...prev, appTabs: newTabs, slotOrder: newOrder };
    });
  };

  const [addTabsSearch, setAddTabsSearch] = useState('');
  const openAddTabsModal = () => { setAddTabsSearch(''); setShowAddTabsModal(true); };
  const addedKeys = useMemo(() => {
    const byName = new Set(formData.appTabs.map((t) => t.name).filter(Boolean));
    const byId = new Set(formData.appTabs.map((t) => t.id).filter(Boolean));
    return { byName, byId };
  }, [formData.appTabs]);
  const availableToAdd = tabs.filter((t) => !addedKeys.byName.has(t.name) && !addedKeys.byId.has(t.id));
  const addTabsSearchLower = (addTabsSearch || '').trim().toLowerCase();
  const availableToAddFiltered = useMemo(() => {
    if (!addTabsSearchLower) return availableToAdd;
    return availableToAdd.filter(
      (t) =>
        (t.label || '').toLowerCase().includes(addTabsSearchLower) ||
        (t.name || '').toLowerCase().includes(addTabsSearchLower) ||
        (t.object_name || '').toLowerCase().includes(addTabsSearchLower)
    );
  }, [availableToAdd, addTabsSearchLower]);

  // Derive tab groups from tabs (group_key from config or top-level); each group has tabs sorted by order_no
  const tabGroups = useMemo(() => {
    const getGroupKey = (t) => (t?.config?.group_key ?? t?.group_key)?.trim?.() || '';
    const getGroupLabel = (t) => (t?.config?.group_label ?? t?.group_label)?.trim?.() || getGroupKey(t) || 'Group';
    const byKey = new Map();
    tabs.forEach((tab) => {
      const gk = getGroupKey(tab);
      if (!gk) return;
      if (!byKey.has(gk)) {
        byKey.set(gk, { group_key: gk, group_label: getGroupLabel(tab), tabs: [] });
      }
      byKey.get(gk).tabs.push(tab);
    });
    byKey.forEach((g) => {
      g.tabs.sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
    });
    return Array.from(byKey.values()).sort((a, b) => (a.group_label || a.group_key).localeCompare(b.group_label || b.group_key));
  }, [tabs]);

  // Groups that have at least one tab not yet in the app
  const groupsWithAvailableTabs = useMemo(() => {
    return tabGroups.filter((g) => g.tabs.some((t) => !addedKeys.byName.has(t.name) && !addedKeys.byId.has(t.id)));
  }, [tabGroups, addedKeys]);

  const addTabsToApp = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;
    const newEntries = selectedIds.map((id) => {
      const tab = getTabById(id);
      return { name: tab?.name ?? '', id, group_key: '', group_label: '' };
    });
    const newSlots = newEntries.map((e) => ({ type: 'tab', name: e.name, id: e.id }));
    setFormData((prev) => ({ ...prev, appTabs: [...prev.appTabs, ...newEntries], slotOrder: [...(prev.slotOrder || []), ...newSlots] }));
  };

  const addGroupToApp = (group) => {
    if (!group?.tabs?.length) return;
    const toAdd = group.tabs
      .filter((t) => !addedKeys.byName.has(t.name) && !addedKeys.byId.has(t.id))
      .map((t) => ({ name: t.name, id: t.id, group_key: group.group_key, group_label: group.group_label || group.group_key }));
    if (toAdd.length === 0) return;
    const groupSlot = { type: 'group', group_key: group.group_key };
    setFormData((prev) => ({ ...prev, appTabs: [...prev.appTabs, ...toAdd], slotOrder: [...(prev.slotOrder || []), groupSlot] }));
  };

  // Groups in display order (order of first appearance); each with its tab entries in order.
  const appTabsByGroup = useMemo(() => {
    const order = [];
    const byKey = new Map();
    formData.appTabs.forEach((entry) => {
      const gk = (entry.group_key || '').trim();
      const key = gk || '__none__';
      if (!byKey.has(key)) {
        byKey.set(key, { group_key: gk, group_label: (entry.group_label || gk || 'No group').trim() || 'No group', entries: [] });
        order.push(key);
      }
      byKey.get(key).entries.push(entry);
    });
    return order.map((key) => byKey.get(key));
  }, [formData.appTabs]);

  // Menu bar slots: one row per tab (no-group) or per group, in slotOrder order — so user can put "Group (WatConnect)" between tabs.
  const barSlots = useMemo(() => {
    const order = formData.slotOrder && formData.slotOrder.length > 0 ? formData.slotOrder : (() => {
      const def = [];
      const groupSeen = new Set();
      formData.appTabs.forEach((entry) => {
        const gk = (entry.group_key || '').trim();
        if (!gk) def.push({ type: 'tab', name: entry.name, id: entry.id });
        else if (!groupSeen.has(gk)) { groupSeen.add(gk); def.push({ type: 'group', group_key: gk }); }
      });
      return def;
    })();
    const slots = [];
    const findEntry = (name, id) => formData.appTabs.find((e) => (name && e.name === name) || (id != null && String(e.id) === String(id)));
    order.forEach((slot) => {
      if (slot.type === 'tab') {
        const entry = findEntry(slot.name, slot.id);
        if (entry) slots.push({ type: 'tab', entry, slot: { type: 'tab', name: entry.name, id: entry.id } });
      } else if (slot.type === 'group' && slot.group_key) {
        const entries = formData.appTabs.filter((e) => (e.group_key || '').trim() === slot.group_key);
        const label = entries[0]?.group_label || slot.group_key;
        if (entries.length > 0) slots.push({ type: 'group', group_key: slot.group_key, group_label: label, entries, slot: { type: 'group', group_key: slot.group_key } });
      }
    });
    return slots;
  }, [formData.appTabs, formData.slotOrder]);

  // Reorder menu bar slots (tab rows and group rows in one list — like Tab Manager)
  const onBarSlotsDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    if (result.source.droppableId !== 'bar-slots-all') return;
    const order = barSlots.map((s) => s.slot);
    const [removed] = order.splice(result.source.index, 1);
    order.splice(result.destination.index, 0, removed);
    setFormData((prev) => ({ ...prev, slotOrder: order }));
  };

  // Reorder tabs within a single group (same pattern as TabManager handleGroupTabsDragEnd)
  const onGroupTabsDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const droppableId = result.source.droppableId;
    if (!droppableId.startsWith('group-tabs-')) return;
    const groupKey = droppableId.replace('group-tabs-', '');
    const groupIndex = appTabsByGroup.findIndex((g) => (g.group_key || '__none__') === groupKey);
    if (groupIndex < 0) return;
    const group = appTabsByGroup[groupIndex];
    const entries = [...group.entries];
    const [removed] = entries.splice(result.source.index, 1);
    entries.splice(result.destination.index, 0, removed);
    const rebuilt = appTabsByGroup.map((g, i) => (i === groupIndex ? { ...g, entries } : g));
    setFormData((prev) => ({ ...prev, appTabs: rebuilt.flatMap((g) => g.entries) }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.droppableId === 'bar-slots-all') {
      onBarSlotsDragEnd(result);
      return;
    }
    if (result.source.droppableId.startsWith('group-tabs-')) {
      onGroupTabsDragEnd(result);
    }
  };

  const renderTabsSummary = (app) => {
    const appTabsList = configToAppTabs(app?.config);
    if (appTabsList.length === 0) return '—';
    const withGroups = appTabsList.filter((t) => (t.group_key || '').trim()).length;
    const tabLabels = appTabsList.map((t) => getTabLabelByEntry(t));
    if (tabLabels.length <= 3) return tabLabels.join(', ');
    return `${tabLabels.slice(0, 2).join(', ')} +${tabLabels.length - 2} more${withGroups ? ` (${withGroups} in groups)` : ''}`;
  };

  if (loading) {
    return (
      <Container fluid className="py-3">
        <div className="d-flex align-items-center">
          <div className="spinner-border text-primary me-2" role="status" />
          <span>Loading apps...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">App Manager</h5>
              {canCreateApp && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <Plus className="me-1" /> New App
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <p className="text-muted small">
                Create apps and assign tabs. Set tab order and optional groups per app (drag to reorder in edit).
              </p>
              {apps.length === 0 ? (
                <Alert variant="info">No apps yet. Click &quot;New App&quot; to create one.</Alert>
              ) : (
                <Table responsive bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: 52 }}>Icon</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Standard</th>
                      <th>Tabs</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((app) => (
                      <tr key={app.id}>
                        <td className="align-middle">
                          <AppIconDisplay icon={app?.config?.icon} size={28} />
                        </td>
                        <td>{app.name}</td>
                        <td>{app.description || '—'}</td>
                        <td>{app.is_standard ? 'Yes' : 'No'}</td>
                        <td>{renderTabsSummary(app)}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(app)}>
                            <Pencil />
                          </Button>
                          {canDeleteApp(app) && (
                            <Button variant="outline-danger" size="sm" onClick={() => confirmDelete(app)}>
                              <Trash2 />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingApp ? 'Edit App' : 'New App'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Sales App"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              placeholder="Short description"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>App icon (optional)</Form.Label>
            <Form.Select
              value={formData.icon || ''}
              onChange={(e) => setFormData((p) => ({ ...p, icon: e.target.value }))}
            >
              <option value="">None</option>
              <option value="preset:default">Default (grid)</option>
              <option value="preset:sales">Sales</option>
              <option value="preset:service">Service</option>
              <option value="preset:marketing">Marketing</option>
              <option value="preset:content">Content</option>
              <option value="preset:realestate">Real Estate</option>
              <option value="custom">Custom URL</option>
            </Form.Select>
            {formData.icon === 'custom' && (
              <Form.Control
                className="mt-2"
                type="url"
                placeholder="https://example.com/icon.png"
                value={formData.iconCustomUrl || ''}
                onChange={(e) => setFormData((p) => ({ ...p, iconCustomUrl: e.target.value }))}
              />
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="app-is-standard"
              label="Standard app"
              checked={!!formData.is_standard}
              onChange={(e) => setFormData((p) => ({ ...p, is_standard: e.target.checked }))}
            />
          </Form.Group>

          <Form.Group>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Tabs in this app (order and groups)</Form.Label>
              <Button variant="outline-primary" size="sm" onClick={openAddTabsModal}>
                <Plus className="me-1" /> Add tabs
              </Button>
            </div>
            <p className="text-muted small mb-2">
              <strong>Menu bar order</strong>: drag rows to place tabs and groups anywhere (e.g. Home, Accounts, <strong>Group (WatConnect)</strong>, Contact). Then reorder tabs <strong>within each group</strong> below.
            </p>
            {formData.appTabs.length === 0 ? (
              <Alert variant="light" className="mb-0">
                No tabs. Click &quot;Add tabs&quot; to add from Tab Manager list.
              </Alert>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                {/* Menu bar order: one row per tab (no group) or per group — like Tab Manager; group can sit between tabs */}
                <div className="mb-4">
                  <h6 className="text-primary mb-2">Menu bar order</h6>
                  <p className="text-muted small mb-2">
                    Drag to reorder. Each row is either a tab or a group; you can place a group between tabs (e.g. Home, Accounts, WatConnect, Contact).
                  </p>
                  <Table bordered size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} aria-label="Drag" />
                        <th>#</th>
                        <th>Tab</th>
                        <th>Group (optional)</th>
                        <th style={{ width: 60 }} />
                      </tr>
                    </thead>
                    <Droppable droppableId="bar-slots-all">
                      {(barProvided) => (
                        <tbody ref={barProvided.innerRef} {...barProvided.droppableProps}>
                          {barSlots.map((slot, index) => {
                            if (slot.type === 'group') {
                              return (
                                <Draggable key={`group-${slot.group_key}`} draggableId={`group-${slot.group_key}`} index={index}>
                                  {(provided) => (
                                    <tr ref={provided.innerRef} {...provided.draggableProps} className="table-secondary">
                                      <td {...provided.dragHandleProps} className="align-middle">
                                        <FaGripVertical className="text-muted" />
                                      </td>
                                      <td className="align-middle">{index + 1}</td>
                                      <td className="align-middle">
                                        <span className="text-muted">Group</span>
                                        <strong className="ms-1">({slot.group_label || slot.group_key})</strong>
                                      </td>
                                      <td className="align-middle text-muted">{slot.entries.length} tab(s)</td>
                                      <td className="align-middle" />
                                    </tr>
                                  )}
                                </Draggable>
                              );
                            }
                            const globalIndex = formData.appTabs.findIndex((e) => e === slot.entry);
                            return (
                              <Draggable key={`tab-${globalIndex}`} draggableId={`tab-${globalIndex}`} index={index}>
                                {(provided, snapshot) => (
                                  <tr ref={provided.innerRef} {...provided.draggableProps} className={snapshot.isDragging ? 'table-primary' : ''}>
                                    <td {...provided.dragHandleProps} className="align-middle">
                                      <FaGripVertical className="text-muted" />
                                    </td>
                                    <td className="align-middle">{index + 1}</td>
                                    <td className="align-middle">{getTabLabelByEntry(slot.entry)}</td>
                                    <td className="align-middle">
                                      <InputGroup size="sm">
                                        <Form.Select
                                          value={slot.entry.group_key || ''}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === '__new__') {
                                              setAppTabGroup(globalIndex, '__new__', '');
                                              return;
                                            }
                                            const g = existingGroupsInApp.find((x) => x.group_key === v);
                                            setAppTabGroup(globalIndex, v, g ? g.group_label : v);
                                          }}
                                        >
                                          <option value="">No group</option>
                                          {existingGroupsInApp.map((g) => (
                                            <option key={g.group_key} value={g.group_key}>
                                              {g.group_label || g.group_key}
                                            </option>
                                          ))}
                                          <option value="__new__">+ New group</option>
                                        </Form.Select>
                                        {slot.entry.group_key === '__new__' && (
                                          <>
                                            <Form.Control
                                              placeholder="Group name (e.g. Sales)"
                                              value={slot.entry.group_label || ''}
                                              onChange={(e) => setAppTabGroup(globalIndex, '__new__', e.target.value)}
                                            />
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                              onClick={() => {
                                                const label = (slot.entry.group_label || '').trim() || 'new';
                                                const key = label.toLowerCase().replace(/\s+/g, '_');
                                                setAppTabGroup(globalIndex, key, label);
                                              }}
                                            >
                                              Set
                                            </Button>
                                          </>
                                        )}
                                      </InputGroup>
                                      {slot.entry.group_key && slot.entry.group_key !== '__new__' && (
                                        <small className="text-muted">{slot.entry.group_label || slot.entry.group_key}</small>
                                      )}
                                    </td>
                                    <td className="align-middle">
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeAppTab(globalIndex)}
                                        title="Remove from app"
                                      >
                                        <Trash2 />
                                      </Button>
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            );
                          })}
                          {barProvided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </Table>
                </div>

                {/* Tabs within each group (like TabManager group-tabs-* per group) */}
                <h6 className="text-muted mb-2">Tabs within each group</h6>
                <p className="text-muted small mb-3">
                  Reorder tabs inside each group. Drag tab rows only within the same section.
                </p>
                {appTabsByGroup.map((group, groupIndex) => {
                  const gkey = group.group_key || '__none__';
                  const droppableId = `group-tabs-${gkey}`;
                  const sectionLabel = gkey === '__none__' ? 'No group' : (group.group_label || group.group_key);
                  return (
                    <div key={gkey} className="mb-3">
                      <h6 className="text-secondary small mb-2">{sectionLabel}</h6>
                      <Table bordered size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: 36 }} aria-label="Drag" />
                            <th>#</th>
                            <th>Tab</th>
                            <th>Group (optional)</th>
                            <th style={{ width: 60 }} />
                          </tr>
                        </thead>
                        <Droppable droppableId={droppableId}>
                          {(droppableProvided) => (
                            <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                              {group.entries.map((entry, index) => {
                                const globalIndex =
                                  appTabsByGroup.slice(0, groupIndex).reduce((acc, g) => acc + g.entries.length, 0) + index;
                                return (
                                  <Draggable
                                    key={entry.name || entry.id || index}
                                    draggableId={String(entry.name || entry.id || `${gkey}-${index}`)}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <tr
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={snapshot.isDragging ? 'table-primary' : ''}
                                      >
                                        <td {...provided.dragHandleProps} className="align-middle">
                                          <FaGripVertical className="text-muted" />
                                        </td>
                                        <td className="align-middle">{globalIndex + 1}</td>
                                        <td className="align-middle">{getTabLabelByEntry(entry)}</td>
                                        <td className="align-middle">
                                          <InputGroup size="sm">
                                            <Form.Select
                                              value={entry.group_key || ''}
                                              onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '__new__') {
                                                  setAppTabGroup(globalIndex, '__new__', '');
                                                  return;
                                                }
                                                const g = existingGroupsInApp.find((x) => x.group_key === v);
                                                setAppTabGroup(globalIndex, v, g ? g.group_label : v);
                                              }}
                                            >
                                              <option value="">No group</option>
                                              {existingGroupsInApp.map((g) => (
                                                <option key={g.group_key} value={g.group_key}>
                                                  {g.group_label || g.group_key}
                                                </option>
                                              ))}
                                              <option value="__new__">+ New group</option>
                                            </Form.Select>
                                            {entry.group_key === '__new__' && (
                                              <>
                                                <Form.Control
                                                  placeholder="Group name (e.g. Sales)"
                                                  value={entry.group_label || ''}
                                                  onChange={(e) => setAppTabGroup(globalIndex, '__new__', e.target.value)}
                                                />
                                                <Button
                                                  variant="outline-secondary"
                                                  size="sm"
                                                  onClick={() => {
                                                    const label = (entry.group_label || '').trim() || 'new';
                                                    const key = label.toLowerCase().replace(/\s+/g, '_');
                                                    setAppTabGroup(globalIndex, key, label);
                                                  }}
                                                >
                                                  Set
                                                </Button>
                                              </>
                                            )}
                                          </InputGroup>
                                          {entry.group_key && entry.group_key !== '__new__' && (
                                            <small className="text-muted">{entry.group_label || entry.group_key}</small>
                                          )}
                                        </td>
                                        <td className="align-middle">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeAppTab(globalIndex)}
                                            title="Remove from app"
                                          >
                                            <Trash2 />
                                          </Button>
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {droppableProvided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      </Table>
                    </div>
                  );
                })}
              </DragDropContext>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingApp ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add tabs modal - stays open until user clicks Done; scrollable body */}
      <Modal show={showAddTabsModal} onHide={() => setShowAddTabsModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Add tabs to app</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {availableToAdd.length === 0 && groupsWithAvailableTabs.length === 0 ? (
            <Alert variant="info">All tabs are already in this app.</Alert>
          ) : (
            <>
              {groupsWithAvailableTabs.length > 0 && (
                <div className="mb-3">
                  <h6 className="text-primary mb-2">Add entire group</h6>
                  <p className="text-muted small mb-2">
                    Add all tabs that belong to a group in one go. They will be added with the group already set.
                  </p>
                  <Table bordered size="sm">
                    <thead>
                      <tr>
                        <th>Group</th>
                        <th>Tabs to add</th>
                        <th style={{ width: 100 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {groupsWithAvailableTabs.map((g) => {
                        const count = g.tabs.filter((t) => !addedKeys.byName.has(t.name) && !addedKeys.byId.has(t.id)).length;
                        return (
                          <tr key={g.group_key}>
                            <td><strong>{g.group_label || g.group_key}</strong></td>
                            <td>{count} tab{count !== 1 ? 's' : ''}</td>
                            <td>
                              <Button variant="outline-primary" size="sm" onClick={() => addGroupToApp(g)}>
                                Add group
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              {availableToAdd.length > 0 && (
                <div>
                  <h6 className="text-primary mb-2">Add individual tabs</h6>
                  <Form.Control
                    type="search"
                    placeholder="Search tabs..."
                    value={addTabsSearch}
                    onChange={(e) => setAddTabsSearch(e.target.value)}
                    className="mb-2"
                    size="sm"
                  />
                  <Table bordered size="sm">
                    <thead>
                      <tr>
                        <th>Tab</th>
                        <th style={{ width: 80 }}>Add</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableToAddFiltered.map((tab) => (
                        <tr key={tab.id}>
                          <td>{tab.label || tab.name}</td>
                          <td>
                            <Button variant="outline-primary" size="sm" onClick={() => addTabsToApp([tab.id])}>
                              Add
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {addTabsSearchLower && availableToAddFiltered.length === 0 && (
                    <Alert variant="light" className="mb-0 mt-2 small">No tabs match &quot;{addTabsSearch}&quot;</Alert>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddTabsModal(false)}>Done</Button>
          {availableToAddFiltered.length > 0 && (
            <Button variant="primary" onClick={() => addTabsToApp(availableToAddFiltered.map((t) => t.id))}>
              Add all {addTabsSearchLower ? `(${availableToAddFiltered.length})` : ''}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete App</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the app &quot;{appToDelete?.name}&quot;? This will not delete the tabs.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AppManager;
